<template>
  <el-drawer
    v-model="visible"
    :title="`设备详情 - ${device?.deviceName || ''}`"
    size="600px"
    destroy-on-close
  >
    <template #default>
      <el-tabs v-model="activeTab" class="device-detail-tabs">
        <!-- 基本信息 -->
        <el-tab-pane label="基本信息" name="info">
          <el-descriptions :column="1" border>
            <el-descriptions-item label="设备ID">
              <el-tag type="info" size="small">{{ device?.deviceId }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="设备名称">
              {{ device?.deviceName }}
            </el-descriptions-item>
            <el-descriptions-item label="设备地址">
              {{ device?.address || '未设置' }}
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">
              {{ device?.createTime }}
            </el-descriptions-item>
            <el-descriptions-item label="当前绑定">
              <template v-if="device?.currentInspector">
                <el-tag type="success" size="small">
                  {{ device.currentInspector.nickName }}
                </el-tag>
                <span class="phone-text">
                  ({{ device.currentInspector.phone }})
                </span>
              </template>
              <el-tag v-else type="info" size="small">未绑定</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="绑定时间">
              {{ device?.currentBindTime || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="历史绑定次数">
              {{ device?.historyCount || 0 }} 次
            </el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>

        <!-- 绑定历史 -->
        <el-tab-pane label="绑定历史" name="history">
          <el-timeline>
            <el-timeline-item
              v-for="(record, index) in historyRecords"
              :key="index"
              :timestamp="record.bindTime"
              :type="record.isCurrent ? 'success' : 'primary'"
              placement="top"
            >
              <el-card shadow="never">
                <div class="history-item">
                  <div class="history-header">
                    <el-tag
                      :type="record.isCurrent ? 'success' : 'info'"
                      size="small"
                    >
                      {{ record.isCurrent ? '当前绑定' : '历史绑定' }}
                    </el-tag>
                    <span class="inspector-name">
                      {{ record.inspectorName }}
                    </span>
                    <span class="inspector-phone">
                      ({{ record.inspectorPhone }})
                    </span>
                  </div>
                  <div class="history-time">
                    <template v-if="record.isCurrent">
                      绑定时间：{{ record.bindTime }} - 至今
                    </template>
                    <template v-else>
                      绑定时间：{{ record.bindTime }} - {{ record.unbindTime || '未知' }}
                    </template>
                  </div>
                </div>
              </el-card>
            </el-timeline-item>
          </el-timeline>

          <el-empty
            v-if="!historyRecords.length"
            description="暂无绑定历史"
          />
        </el-tab-pane>

        <!-- 数据统计 -->
        <el-tab-pane label="数据统计" name="statistics">
          <el-row :gutter="20" class="statistics-row">
            <el-col :span="8">
              <el-statistic
                title="总数据条数"
                :value="statistics.totalCount"
                prefix-icon="DataLine"
              />
            </el-col>
            <el-col :span="8">
              <el-statistic
                title="检测员A数据"
                :value="statistics.inspectorACount"
                prefix-icon="User"
              />
            </el-col>
            <el-col :span="8">
              <el-statistic
                title="检测员B数据"
                :value="statistics.inspectorBCount"
                prefix-icon="UserFilled"
              />
            </el-col>
          </el-row>

          <el-divider content-position="center">数据分布</el-divider>

          <el-table :data="statistics.dataList" border style="width: 100%">
            <el-table-column prop="dataName" label="数据项" width="100" />
            <el-table-column prop="inspectorAValue" label="检测员A" align="center">
              <template #default="{ row }">
                {{ row.inspectorAValue || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="inspectorBValue" label="检测员B" align="center">
              <template #default="{ row }">
                {{ row.inspectorBValue || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="latestValue" label="最新值" align="center">
              <template #default="{ row }">
                <el-tag type="primary" size="small">
                  {{ row.latestValue || '-' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </template>

    <!-- 底部操作栏 -->
    <template #footer>
      <div class="drawer-footer">
        <el-button @click="visible = false">关闭</el-button>
        <el-button
          v-if="permission !== 2"
          type="warning"
          @click="handleExport"
        >
          导出历史数据
        </el-button>
        <el-button
          v-if="permission !== 2 && device?.currentInspector"
          type="danger"
          @click="handleUnbind"
        >
          解绑设备
        </el-button>
        <el-button
          v-if="permission !== 2"
          type="primary"
          @click="handleTransfer"
        >
          转移设备
        </el-button>
      </div>
    </template>
  </el-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getDeviceHistory,
  getDeviceStatistics,
  unbindDevice
} from '@/api/device'

export default {
  name: 'DeviceDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    device: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:visible', 'transfer', 'export', 'unbind'],
  setup(props, { emit }) {
    const activeTab = ref('info')
    const historyRecords = ref([])
    const statistics = ref({
      totalCount: 0,
      inspectorACount: 0,
      inspectorBCount: 0,
      dataList: []
    })
    const permission = ref(null)

    // 同步 visible
    const visible = computed({
      get: () => props.visible,
      set: (val) => emit('update:visible', val)
    })

    // 加载绑定历史
    const loadHistory = async () => {
      if (!props.device?.deviceId) return

      try {
        const response = await getDeviceHistory(props.device.deviceId)
        if (response.code === 200) {
          historyRecords.value = response.data || []
        }
      } catch (error) {
        console.error('获取绑定历史失败：', error)
      }
    }

    // 加载数据统计
    const loadStatistics = async () => {
      if (!props.device?.deviceId) return

      try {
        const response = await getDeviceStatistics(props.device.deviceId)
        if (response.code === 200) {
          statistics.value = response.data || {
            totalCount: 0,
            inspectorACount: 0,
            inspectorBCount: 0,
            dataList: []
          }
        }
      } catch (error) {
        console.error('获取数据统计失败：', error)
      }
    }

    // 监听 visible 变化
    watch(() => props.visible, (val) => {
      if (val) {
        activeTab.value = 'info'
        loadHistory()
        loadStatistics()
      }
    })

    // 解绑设备
    const handleUnbind = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要解绑设备 "${props.device.deviceName}" 吗？解绑后该设备将变为未绑定状态。`,
          '解绑确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await unbindDevice({
          deviceId: props.device.deviceId,
          inspectorId: props.device.currentInspector?.id
        })

        if (response.code === 200) {
          ElMessage.success('解绑成功')
          emit('unbind')
          visible.value = false
        } else {
          ElMessage.error(response.message || '解绑失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('解绑失败：', error)
          ElMessage.error('解绑失败')
        }
      }
    }

    // 导出数据
    const handleExport = () => {
      emit('export', props.device)
    }

    // 转移设备
    const handleTransfer = () => {
      if (!props.device?.currentInspector) {
        ElMessage.warning('该设备当前未绑定，无法进行转移操作')
        return
      }
      emit('transfer', props.device)
    }

    // 初始化权限
    permission.value = JSON.parse(localStorage.getItem('permission') || '2')

    return {
      visible,
      activeTab,
      historyRecords,
      statistics,
      permission,
      handleUnbind,
      handleExport,
      handleTransfer
    }
  }
}
</script>

<style lang="scss" scoped>
.device-detail-tabs {
  padding: 0 10px;

  .phone-text {
    margin-left: 8px;
    color: #909399;
    font-size: 12px;
  }

  .history-item {
    .history-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 8px;

      .inspector-name {
        font-weight: 600;
        color: #303133;
      }

      .inspector-phone {
        color: #909399;
        font-size: 12px;
      }
    }

    .history-time {
      color: #909399;
      font-size: 12px;
    }
  }

  .statistics-row {
    margin-bottom: 20px;
  }
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
